import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Download, FileText, Search, Archive as ArchiveIcon, RotateCcw, Layers, AlertCircle, Eye } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const fmtSize = (n) => {
  if (!n && n !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const PREVIEW_MIMES = new Set(["application/pdf", "image/png", "image/jpeg", "image/webp", "text/plain"]);

async function apiDownload(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename || "documento";
  document.body.appendChild(a); a.click();
  setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 200);
}

async function apiOpen(path) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export default function DocumentsPage() {
  const { has, user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const [catId, setCatId] = useState(sp.get("category_id") || "");
  const [subjId] = useState(sp.get("subject_id") || "");
  const [pracId] = useState(sp.get("practice_id") || "");
  const [showUpload, setShowUpload] = useState(false);
  const [showVersion, setShowVersion] = useState(null);
  const [showVersions, setShowVersions] = useState(null);
  const [versionsList, setVersionsList] = useState([]);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (catId) params.category_id = catId;
    if (subjId) params.subject_id = subjId;
    if (pracId) params.practice_id = pracId;
    const r = await api.get("/documents", { params });
    setItems(r.data);
  };
  useEffect(() => {
    api.get("/document-categories").then(r => setCategories(r.data.filter(c => c.is_active)));
    load();
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [q, catId]); // eslint-disable-line

  const archive = async (d) => {
    if (!window.confirm(`Archiviare il documento "${d.name}" (v${d.version})? Il file resta preservato e può essere ripristinato da un amministratore.`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Documento archiviato."); load(); } catch { /* toast */ }
  };

  const openVersions = async (d) => {
    setShowVersions(d);
    const r = await api.get(`/documents/${d.id}/versions`);
    setVersionsList(r.data);
  };

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <h1 className="ma-h1" data-testid="documents-title"><FileText size={22} style={{marginRight:8, verticalAlign:"middle"}}/> Documenti</h1>
          <div className="ma-sub">Archiviazione autenticata su volume persistente, versionamento e audit.</div>
        </div>
        <div className="ma-actions">
          {has("documents.write") && (
            <button className="ma-btn-primary-sm" onClick={() => setShowUpload(true)} data-testid="btn-upload-doc"><Upload size={14}/> Carica documento</button>
          )}
        </div>
      </div>

      <div className="ma-filters">
        <div className="ma-search-wrap"><Search size={14}/>
          <input className="ma-input-sm" placeholder="Cerca nome, descrizione, note…" value={q} onChange={e=>setQ(e.target.value)} data-testid="doc-search"/>
        </div>
        <select className="ma-input-sm" value={catId} onChange={e=>setCatId(e.target.value)} data-testid="doc-cat-filter">
          <option value="">Tutte le categorie</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead><tr>
            <th>Nome</th><th>Categoria</th><th>Versione</th>
            <th>Cliente</th><th>Pratica</th>
            <th>Dimensione</th><th>Autore</th><th>Data</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="9" className="ma-empty">Nessun documento presente.</td></tr>}
            {items.map(d => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                <td>
                  <div className="ma-doc-name">{d.name}</div>
                  {d.file?.original_filename && <div className="ma-mono ma-sub-xs">{d.file.original_filename}</div>}
                </td>
                <td>{d.category?.label || "—"}</td>
                <td>v{d.version}{d.is_current && <span className="ma-pill pill-ok" style={{marginLeft:6}}>corrente</span>}</td>
                <td>{d.subject_display_name ? <Link to={`/clients/${d.subject_id}`} className="ma-link">{d.subject_display_name}</Link> : "—"}</td>
                <td>{d.practice_code ? <Link to={`/practices/${d.practice_id}`} className="ma-link ma-mono">{d.practice_code}</Link> : "—"}</td>
                <td>{fmtSize(d.file?.size_bytes)}</td>
                <td>{d.uploaded_by_name || "—"}</td>
                <td>{new Date(d.created_at).toLocaleString("it-IT")}</td>
                <td className="ma-row-actions">
                  {d.file && PREVIEW_MIMES.has((d.file.mime_type || "").toLowerCase()) && (
                    <button className="ma-btn-link" title="Anteprima" onClick={() => apiOpen(`/documents/${d.id}/preview`)} data-testid={`btn-preview-${d.id}`}>
                      <Eye size={14}/>
                    </button>
                  )}
                  <button className="ma-btn-link" title="Scarica" onClick={() => apiDownload(`/documents/${d.id}/download`, d.file?.original_filename || d.name)} data-testid={`btn-download-${d.id}`}>
                    <Download size={14}/>
                  </button>
                  <button className="ma-btn-link" title="Versioni" onClick={() => openVersions(d)} data-testid={`btn-versions-${d.id}`}>
                    <Layers size={14}/>
                  </button>
                  {has("documents.write") && (
                    <button className="ma-btn-link" title="Nuova versione" onClick={() => setShowVersion(d)} data-testid={`btn-newver-${d.id}`}>
                      <Upload size={14}/>
                    </button>
                  )}
                  {has("documents.write") && (
                    <button className="ma-btn-link ma-danger" title="Archivia" onClick={() => archive(d)} data-testid={`btn-archive-doc-${d.id}`}>
                      <ArchiveIcon size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && <UploadModal categories={categories} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {showVersion && <VersionModal doc={showVersion} onClose={() => setShowVersion(null)} onDone={() => { setShowVersion(null); load(); }} />}
      {showVersions && (
        <VersionsListModal doc={showVersions} versions={versionsList} onClose={() => setShowVersions(null)} />
      )}
    </div>
  );
}

function UploadModal({ categories, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [practiceId, setPracticeId] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [practices, setPractices] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subjects?limit=200").then(r => setSubjects(r.data.items || [])).catch(() => setSubjects([]));
    api.get("/practices?limit=200").then(r => setPractices(r.data.items || [])).catch(() => setPractices([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!file) { setErr("Seleziona un file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    if (description) fd.append("description", description);
    if (categoryId) fd.append("category_id", categoryId);
    if (subjectId) fd.append("subject_id", subjectId);
    if (practiceId) fd.append("practice_id", practiceId);
    if (issueDate) fd.append("issue_date", issueDate);
    if (expiryDate) fd.append("expiry_date", expiryDate);
    setBusy(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato."); onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  return (
    <div className="ma-modal-backdrop" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <h3 className="ma-h3">Carica documento</h3>
        <form onSubmit={submit} className="ma-form">
          <label className="ma-label">File *</label>
          <input type="file" className="ma-input" onChange={e=>setFile(e.target.files?.[0])} required data-testid="upload-file"/>
          <label className="ma-label">Nome visibile</label>
          <input className="ma-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Se vuoto usa il nome del file" data-testid="upload-name"/>
          <label className="ma-label">Categoria *</label>
          <select className="ma-input" value={categoryId} onChange={e=>setCategoryId(e.target.value)} required data-testid="upload-cat">
            <option value="">— Seleziona —</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <div className="ma-form-grid">
            <div><label className="ma-label">Cliente</label>
              <select className="ma-input" value={subjectId} onChange={e=>setSubjectId(e.target.value)}>
                <option value="">—</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
              </select>
            </div>
            <div><label className="ma-label">Pratica</label>
              <select className="ma-input" value={practiceId} onChange={e=>setPracticeId(e.target.value)}>
                <option value="">—</option>
                {practices.map(p => <option key={p.id} value={p.id}>{p.code} — {p.title}</option>)}
              </select>
            </div>
            <div><label className="ma-label">Data emissione</label>
              <input type="date" className="ma-input" value={issueDate} onChange={e=>setIssueDate(e.target.value)}/>
            </div>
            <div><label className="ma-label">Scadenza</label>
              <input type="date" className="ma-input" value={expiryDate} onChange={e=>setExpiryDate(e.target.value)}/>
            </div>
          </div>
          <label className="ma-label">Descrizione</label>
          <textarea className="ma-input" rows={2} value={description} onChange={e=>setDescription(e.target.value)}/>
          {err && <div className="ma-error"><AlertCircle size={14}/> {typeof err === "string" ? err : "Errore"}</div>}
          <div className="ma-form-actions">
            <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="btn-do-upload">{busy ? "Caricamento…" : "Carica"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VersionModal({ doc, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!file) { setErr("Seleziona un file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (notes) fd.append("notes", notes);
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nuova versione creata."); onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <div className="ma-modal-backdrop" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <h3 className="ma-h3">Nuova versione — {doc.name}</h3>
        <p className="ma-sub-desc">La versione precedente (v{doc.version}) sarà conservata nello storico ma non sarà più marcata come corrente.</p>
        <form onSubmit={submit} className="ma-form">
          <label className="ma-label">Nuovo file *</label>
          <input type="file" className="ma-input" onChange={e=>setFile(e.target.files?.[0])} required data-testid="version-file"/>
          <label className="ma-label">Note versione</label>
          <textarea className="ma-input" rows={3} value={notes} onChange={e=>setNotes(e.target.value)}/>
          {err && <div className="ma-error"><AlertCircle size={14}/> {typeof err === "string" ? err : "Errore"}</div>}
          <div className="ma-form-actions">
            <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="btn-do-newver">{busy ? "Caricamento…" : "Sostituisci"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VersionsListModal({ doc, versions, onClose }) {
  return (
    <div className="ma-modal-backdrop" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <h3 className="ma-h3">Storico versioni — {doc.name}</h3>
        <table className="ma-table" data-testid="versions-table">
          <thead><tr><th>v.</th><th>File</th><th>Dim.</th><th>Autore</th><th>Data</th><th></th></tr></thead>
          <tbody>
            {versions.map(v => (
              <tr key={v.id} data-testid={`version-${v.version}`}>
                <td>v{v.version}{v.is_current && <span className="ma-pill pill-ok" style={{marginLeft:4}}>corrente</span>}</td>
                <td className="ma-mono ma-sub-xs">{v.file?.original_filename}</td>
                <td>{fmtSize(v.file?.size_bytes)}</td>
                <td>{v.uploaded_by_name || "—"}</td>
                <td>{new Date(v.created_at).toLocaleString("it-IT")}</td>
                <td>
                  <button className="ma-btn-link" onClick={() => apiDownload(`/documents/${v.id}/download`, v.file?.original_filename || v.name)}>
                    <Download size={14}/> Scarica
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
